import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";

const T = [
  {
    quote:
      "Edufly handled my CAS, visa, and even my accommodation near campus. I just packed my bags and boarded the flight.",
    name: "Mohd Saim Aziz",
    school: "Queen's University Belfast",
    country: "United Kingdom",
    img: "/testimonials/saim.jpg",
  },
  {
    quote:
      "Studying abroad felt like a big leap, but Edufly made it feel small. Admission, visa, even my PESEL and flat near campus — sab kuch unhone sambhal liya.",
    name: "Mohd Wajahat Beg",
    school: "Vistula University, Warsaw",
    country: "Poland",
    img: "/testimonials/wajahat.jpg",
  },
  {
    quote:
      "Italy ka admission process kaafi complex tha, but Edufly ne har document, har deadline sambhal li. Visa interview mein main pehle se confident tha.",
    name: "Arsalaan Memon",
    school: "Sapienza University of Rome",
    country: "Italy",
    img: "/testimonials/arsalaan.jpg",
  },
  {
    quote:
      "Yahan aake pata chala sahi guidance kya hoti hai — scholarship essay se lekar IRP appointment tak, Edufly har step par mere saath tha.",
    name: "Shristi Tomar",
    school: "University College Dublin",
    country: "Ireland",
    img: "/testimonials/shristi.png",
  },
  {
    quote:
      "Shortlisting se lekar visa tak sab kuch itna smooth tha ki kabhi laga hi nahi ki main abroad apply kar raha hoon.",
    name: "Premjeet",
    school: "University of Warsaw",
    country: "Poland",
    img: "/testimonials/premjeet.png",
  },
  {
    quote:
      "Zero tuition waali university ka sapna Edufly ne sach kiya — blocked account se lekar enrollment tak poora process unhone handle kiya.",
    name: "Kartik Tyagi",
    school: "University of Berlin",
    country: "Germany",
    img: "/testimonials/kartik.png",
  },
];

export default function Testimonials() {
  const [i, setI] = useState(0);
  const next = () => setI((v) => (v + 1) % T.length);
  const prev = () => setI((v) => (v - 1 + T.length) % T.length);

  const cur = T[i];

  return (
    <section
      id="testimonials"
      className="relative py-24 md:py-32 bg-[#F5F7FA] overflow-hidden"
      data-testid="testimonials-section"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-14 md:mb-20">
          <div className="md:col-span-4">
            <span className="section-label">CH · 05 / Voices</span>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-display text-[#0a192f] text-4xl sm:text-5xl md:text-6xl tracking-[-0.025em] leading-[0.98]">
              Departures we{" "}
              <span className="italic font-normal">helped happen.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden clip-notched bg-[#0a192f]">
              <AnimatePresence mode="wait">
                <motion.img
                  key={cur.img}
                  src={cur.img}
                  alt={cur.name}
                  initial={{ opacity: 0, scale: 1.08 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.02 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </AnimatePresence>
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between font-mono-tech text-white/85 text-[10px]">
                <span>◈ ALUMNUS</span>
                <span>0{i + 1} / 0{T.length}</span>
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            <Quote size={52} className="text-[#00A3FF] mb-6" strokeWidth={1.2} />
            <AnimatePresence mode="wait">
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="font-display text-2xl md:text-4xl leading-[1.15] tracking-[-0.015em] text-[#0a192f] max-w-2xl mb-10">
                  "{cur.quote}"
                </p>
                <div className="border-t border-[#0a192f]/15 pt-6 flex items-center justify-between max-w-2xl">
                  <div>
                    <div className="font-display text-lg text-[#0a192f]">
                      {cur.name}
                    </div>
                    <div className="font-mono-tech text-[11px] text-[#0a192f]/60 tracking-wider uppercase mt-1">
                      {cur.school} · {cur.country}
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="flex items-center gap-3 mt-10">
              <button
                onClick={prev}
                className="w-12 h-12 rounded-full border border-[#0a192f]/20 hover:bg-[#0a192f] hover:text-white transition-colors flex items-center justify-center"
                aria-label="previous testimonial"
                data-testid="testimonial-prev"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={next}
                className="w-12 h-12 rounded-full border border-[#0a192f]/20 hover:bg-[#0a192f] hover:text-white transition-colors flex items-center justify-center"
                aria-label="next testimonial"
                data-testid="testimonial-next"
              >
                <ChevronRight size={18} />
              </button>
              <div className="ml-4 font-mono-tech text-[11px] text-[#0a192f]/50 tracking-wider">
                {String(i + 1).padStart(2, "0")} / {String(T.length).padStart(2, "0")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
